import { useAuth } from '@/contexts';
import { ORGANIZATION_PERMISSION_TYPE, PERMISSION_TYPE } from '@/enums';
import { SidebarConfigItem, SidebarItemType } from '@/types';

import { sidebarConfig } from '../layout/Sidebar/SidebarConfig';

type SidebarPermissionChecks = {
  hasAllPermissions: (permissions: PERMISSION_TYPE[]) => boolean;
  hasAllOrganizationPermissions: (permissions: ORGANIZATION_PERMISSION_TYPE[]) => boolean;
};

const hasRequiredAccess = (configItem: SidebarConfigItem, checks: SidebarPermissionChecks) => {
  const hasRequiredPermissions =
    !configItem.requiredPermissions || checks.hasAllPermissions(configItem.requiredPermissions);

  const hasRequiredOrganizationPermissions =
    !configItem.requiredOrganizationPermissions ||
    checks.hasAllOrganizationPermissions(configItem.requiredOrganizationPermissions);

  const isHiddenByAllPermissions =
    !!configItem.hiddenWhenAllPermissions &&
    checks.hasAllPermissions(configItem.hiddenWhenAllPermissions);

  return hasRequiredPermissions && hasRequiredOrganizationPermissions && !isHiddenByAllPermissions;
};

const getVisibleSidebarItems = (
  items: SidebarConfigItem[],
  checks: SidebarPermissionChecks
): SidebarItemType[] =>
  items.flatMap((item) => {
    const isVisible = hasRequiredAccess(item, checks);

    if (!isVisible) {
      return [];
    }

    const children = item.children ? getVisibleSidebarItems(item.children, checks) : undefined;

    if (item.children?.length && !children?.length) {
      return [];
    }

    return [
      {
        label: item.label,
        icon: item.icon,
        path: item.path,
        children
      }
    ];
  });

export const useSidebarItems = (): SidebarItemType[] => {
  const { hasAllPermissions, hasAllOrganizationPermissions } = useAuth();

  const checks: SidebarPermissionChecks = {
    hasAllPermissions,
    hasAllOrganizationPermissions
  };

  return getVisibleSidebarItems(sidebarConfig, checks);
};
